<template>
  <div class="wildlife-form">
    <div v-if="notification.show" class="notification" :class="notification.type">
      {{ notification.message }}
      <button @click="closeNotification" class="close-notification">&times;</button>
    </div>
    <form @submit.prevent="submitForm">
      <!-- Company and Tenure # (1-2) -->
      <div class="form-row">
        <div class="form-group">
          <label for="company_name">Company</label>
          <input type="text" id="company_name" v-model="formData.company_name" disabled />
        </div>
        
        <div class="form-group">
          <label for="tenure_number">Tenure #</label>
          <input type="text" id="tenure_number" v-model="formData.tenure_number" disabled />
        </div>
      </div>
      
      <!-- Heliski Zone (3) -->
      <div class="form-group">
        <label>Heliski Zone</label>
        <multi-select-zone 
          :zones="zones" 
          v-model="formData.selectedZones"
          @input="handleZonesChange"
          :isNoObservationMode="isNoObservation"
        />
      </div>
      
      <!-- Date and Surveyor Name (4-5) -->
      <div class="form-row">
        <div class="form-group">
          <label>Date</label>
          <DatePicker v-model="formData.date" />
        </div>
        
        <div class="form-group">
          <label for="surveyor_name">Surveyor Name</label>
          <input type="text" id="surveyor_name" v-model="formData.surveyor_name" placeholder="Surveyor Name" />
        </div>
      </div>
      
      <!-- Species and Animal Count (6-7) -->
      <div class="form-row">
        <div class="form-group">
          <standardized-dropdown
            id="species"
            label="Species"
            :options="dropdownOptions.SPECIES_OPTIONS"
            v-model="formData.species"
            placeholder="Select Species"
            :required="true"
            @input="handleSpeciesChange"
            :validationRules="validateSpecies"
          />
        </div>
        
        <div class="form-group">
          <label for="animal_count">Animal Count</label>
          <input 
            type="number" 
            id="animal_count" 
            v-model="formData.animal_count" 
            placeholder="Animal Count"
            :disabled="isNoObservation"
          />
        </div>
      </div>
      
      <!-- UTM Coordinates (8-10) -->
      <div class="form-row">
        <div class="form-group">
          <standardized-dropdown
            id="utm_zone"
            label="UTM Zone"
            :options="dropdownOptions.UTM_ZONE_OPTIONS"
            v-model="formData.utm_zone"
            placeholder="Select UTM Zone"
            :validationRules="validateUTMZone"
          />
        </div>
        
        <div class="form-group">
          <label for="utm_e">UTM E</label>
          <input 
            type="text" 
            id="utm_e" 
            v-model="formData.utm_e" 
            placeholder="6-digit number"
            @input="validateUTME"
          />
          <div v-if="validationErrors.utm_e" class="validation-error">
            {{ validationErrors.utm_e }}
          </div>
        </div>
        
        <div class="form-group">
          <label for="utm_n">UTM N</label>
          <input 
            type="text" 
            id="utm_n" 
            v-model="formData.utm_n" 
            placeholder="7-digit number"
            @input="validateUTMN"
          />
          <div v-if="validationErrors.utm_n" class="validation-error">
            {{ validationErrors.utm_n }}
          </div>
        </div>
      </div>
      
      <!-- Comments (11) -->
      <div class="form-group">
        <label for="comments">Comments</label>
        <textarea 
          id="comments" 
          v-model="formData.comments" 
          placeholder="Comments"
          rows="3"
        ></textarea>
      </div>
      
      <!-- Animal Distance and Aspect (12-13) -->
      <div class="form-row">
        <div class="form-group">
          <standardized-dropdown
            id="animal_distance"
            label="Animal Distance"
            :options="dropdownOptions.ANIMAL_DISTANCE_OPTIONS"
            v-model="formData.animal_distance"
            placeholder="Select Animal Distance"
            :disabled="isNoObservation"
            :validationRules="validateAnimalDistance"
          />
        </div>
        
        <div class="form-group">
          <standardized-dropdown
            id="aspect_of_animal"
            label="Aspect of Animal"
            :options="dropdownOptions.ASPECT_OF_ANIMAL_OPTIONS"
            v-model="formData.aspect_of_animal"
            placeholder="Select Aspect"
            :disabled="isNoObservation"
            :validationRules="validateAspectOfAnimal"
          />
        </div>
      </div>
      
      <!-- Elevation and Animal Reaction (14-15) -->
      <div class="form-row">
        <div class="form-group">
          <standardized-dropdown
            id="elevation"
            label="Elevation"
            :options="dropdownOptions.ELEVATION_OPTIONS"
            v-model="elevationDisplay"
            placeholder="Select Elevation"
            :disabled="isNoObservation"
            :validationRules="validateElevation"
          />
        </div>
        
        <div class="form-group">
          <standardized-dropdown
            id="animal_reaction"
            label="Animal Reaction"
            :options="dropdownOptions.ANIMAL_REACTION_OPTIONS"
            v-model="formData.animal_reaction"
            placeholder="Select Reaction"
            :disabled="isNoObservation"
            :validationRules="validateAnimalReaction"
          />
        </div>
      </div>
      
      <!-- Immediate and End of Day Actions (16-17) -->
      <div class="form-row">
        <div class="form-group">
          <standardized-dropdown
            id="immediate_action"
            label="Immediate Action"
            :options="dropdownOptions.ACTION_OPTIONS"
            v-model="formData.immediate_action"
            placeholder="Select Immediate Action"
            :disabled="isNoObservation"
            :validationRules="validateAction"
          />
        </div>

        <div class="form-group">
          <standardized-dropdown
            id="end_of_day_action"
            label="End of Day Action"
            :options="dropdownOptions.ACTION_OPTIONS"
            v-model="formData.end_of_day_action"
            placeholder="Select End of Day Action"
            :disabled="isNoObservation"
            :validationRules="validateAction"
          />
        </div>
      </div>
      
      <!-- Inventory Method and Ski Run (18-19) -->
      <div class="form-row">
        <div class="form-group">
          <standardized-dropdown
            id="inventory_method"
            label="Inventory Method"
            :options="dropdownOptions.INVENTORY_METHOD_OPTIONS"
            v-model="formData.inventory_method"
            placeholder="Select Inventory Method"
            :validationRules="validateInventoryMethod"
          />
        </div>
        
        <div class="form-group">
          <ski-run-field
            v-model="formData.ski_run"
            :selected-zones="formData.selectedZones"
            :runs="runs"
            :species="formData.species"
            :required="false"
          />
        </div>
      </div>
      
      <!-- Animal Tracks and Machine Type (20-21) -->
      <div class="form-row">
        <div class="form-group">
          <label for="num_of_animal_tracks"># of Animal Tracks</label>
          <input 
            type="number" 
            id="num_of_animal_tracks" 
            v-model="formData.num_of_animal_tracks" 
            placeholder="# of Animal Tracks"
            :disabled="isNoObservation"
          />
        </div>
        
        <div class="form-group">
          <standardized-dropdown
            id="type_of_machine"
            label="Type of Machine"
            :options="dropdownOptions.MACHINE_TYPE_OPTIONS"
            v-model="formData.type_of_machine"
            placeholder="Select Machine Type"
            :validationRules="validateMachineType"
          />
        </div>
      </div>
      
      <!-- Snowmobile Observations (22-23) -->
      <div class="form-row checkbox-row">
        <div class="form-group checkbox-group">
          <input
            type="checkbox"
            id="snowmobile_observed"
            v-model="formData.snowmobile_observed"
          />
          <label for="snowmobile_observed">Snowmobile Observed</label>
        </div>

        <div class="form-group checkbox-group">
          <input
            type="checkbox"
            id="snowmobile_track_observed"
            v-model="formData.snowmobile_track_observed"
          />
          <label for="snowmobile_track_observed">Snowmobile Track Observed</label>
        </div>
      </div>
      
      <div class="form-actions">
        <button type="submit" class="submit-button" :disabled="!isFormValid">
          Submit Wildlife Report
        </button>
        <button type="button" class="cancel-button" @click="cancelForm">
          Cancel
        </button>
      </div>
      
      <div v-if="formError" class="form-error">
        {{ formError }}
      </div>
    </form>
  </div>
</template>

<script>
import MultiSelectZone from './MultiSelectZone.vue';
import StandardizedDropdown from './StandardizedDropdown.vue';
import SkiRunField from './SkiRunField.vue';
import { validateUTME, validateUTMN } from '../../utils/coordinateValidation';
import { validateWildlifeReport, prepareWildlifeReportData, isNoObservation, getElevationName } from '../../utils/wildlifeUtils';
import StaffDataService from '@/services/StaffDataService.js';
import { 
  validateSpecies, 
  validateUTMZone, 
  validateAnimalDistance, 
  validateAspectOfAnimal, 
  validateElevation, 
  validateAnimalReaction, 
  validateAction, 
  validateInventoryMethod, 
  validateMachineType 
} from '../../utils/dropdownValidation';
import { WILDLIFE_DROPDOWN_OPTIONS } from '../../constants/wildlifeDropdowns';
import { WILDLIFE_FORM_DEFAULT_VALUES } from '../../constants/wildlifeFormConfig';
import DatePicker from '../Shared/DatePicker.vue';
import { todayLocalDateString } from '../../utils/dateHelpers';

export default {
  name: 'WildlifeForm',
  components: {
    MultiSelectZone,
    StandardizedDropdown,
    SkiRunField,
    DatePicker
  },
  props: {
    zones: {
      type: Array,
      required: true
    },
    runs: {
      type: Array,
      required: true
    },
    tripGroupId: {
      type: [Number, String],
      required: true
    },
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        ...WILDLIFE_FORM_DEFAULT_VALUES,
        ...this.initialData,
        tripgroupid: this.tripGroupId,
        selectedZones: [],
        date: sessionStorage.getItem('selectedDate') || todayLocalDateString(),
        observerid: null
      },
      dropdownOptions: WILDLIFE_DROPDOWN_OPTIONS,
      validationErrors: {
        utm_e: '',
        utm_n: '',
        form: ''
      },
      formError: '',
      notification: {
        show: false,
        type: '',
        message: ''
      }
    };
  },
  computed: {
    isNoObservation() {
      return isNoObservation(this.formData.species);
    },
    // Computed property to handle elevation conversion
    elevationDisplay: {
      get() {
        // If elevation is a number (from database), convert it to string for display
        if (typeof this.formData.elevation === 'number') {
          return getElevationName(this.formData.elevation);
        }
        return this.formData.elevation;
      },
      set(value) {
        // Store the string value directly, it will be converted to integer when submitting
        this.formData.elevation = value;
      }
    },
    isFormValid() {
      // Check if there are any validation errors
      const hasValidationErrors = Object.values(this.validationErrors).some(error => error !== '');

      // Check if required fields are filled
      const requiredFieldsFilled = this.formData.species &&
                                  this.formData.date &&
                                  this.formData.selectedZones.length > 0;

      // Ski run is now optional for all reports

      // Validate all dropdown selections against standardized options
      const dropdownValidationResult = this.validateAllDropdowns();

      return !hasValidationErrors && requiredFieldsFilled && dropdownValidationResult.isValid;
    }
  },
  methods: {
    // Expose validation functions to template
    validateSpecies,
    validateUTMZone,
    validateAnimalDistance,
    validateAspectOfAnimal,
    validateElevation,
    validateAnimalReaction,
    validateAction,
    validateInventoryMethod,
    validateMachineType,

    fetchStaffId() {
      // Check if person ID is already in sessionStorage
      const existingPersonId = sessionStorage.getItem('personId');
      if (existingPersonId) {
        this.formData.observerid = parseInt(existingPersonId);
        return;
      }

      // If not in sessionStorage, fetch it using the email
      const userEmail = sessionStorage.getItem('email');
      if (userEmail) {
        StaffDataService.findByEmail(userEmail)
          .then(response => {
            const personId = response.data.personid;
            this.formData.observerid = personId;
            // Store in sessionStorage for future use
            sessionStorage.setItem('personId', personId);
            // Also store staffId for other parts of the app that might need it
            sessionStorage.setItem('staffId', response.data.staffid);
          })
          .catch(error => {
            console.error('Error fetching staff/person ID:', error);
            // If we can't fetch person ID, leave observerid as null
          });
      }
    },

    validateUTME() {
      const result = validateUTME(this.formData.utm_e);
      this.validationErrors.utm_e = result.isValid ? '' : result.message;
    },
    validateUTMN() {
      const result = validateUTMN(this.formData.utm_n);
      this.validationErrors.utm_n = result.isValid ? '' : result.message;
    },
    handleSpeciesChange(species) {
      const wasNoObservation = isNoObservation(this.formData.species);
      const isNowNoObservation = isNoObservation(species);

      // If species is "No Animal Observation", reset animal-specific fields
      // BUT keep snowmobile checkboxes enabled (don't reset them)
      if (isNowNoObservation) {
        this.formData.animal_count = '';
        this.formData.animal_distance = '';
        this.formData.aspect_of_animal = '';
        this.formData.animal_reaction = '';
        this.formData.immediate_action = '';
        this.formData.end_of_day_action = '';
        this.formData.num_of_animal_tracks = '';
        // NOTE: Snowmobile checkboxes are NOT reset - guides can still report snowmobile sightings
        this.formData.ski_run = ''; // Clear ski run for "No Animal Observation"

        // Set default comment if empty
        if (!this.formData.comments) {
          this.formData.comments = 'No wildlife observed in selected zones';
        }

        // If changing from wildlife observation to "No Animal Observation", show helpful message
        if (!wasNoObservation) {
          this.showNotification('info', 'You can now select multiple zones for "No Animal Observation" reporting.');
        }
      } else {
        // If changing from "No Animal Observation" to a wildlife observation and multiple zones are selected,
        // keep only the first zone to comply with validation rules
        if (this.formData.selectedZones.length > 1) {
          const firstZone = this.formData.selectedZones[0];
          this.formData.selectedZones = [firstZone];
          this.showNotification('warning', 'Multiple zones are only allowed for "No Animal Observation". Only the first zone has been kept.');
        } else if (this.formData.selectedZones.length === 0) {
          // If no zones are selected, remind the user to select a zone
          this.showNotification('warning', 'Please select a zone for this wildlife observation.');
        }

        // If changing from "No Animal Observation" to wildlife observation, remind about ski run requirement
        if (wasNoObservation) {
          this.showNotification('info', 'For wildlife observations, a specific ski run must be provided.');
        }
      }
    },
    handleZonesChange(zones) {
      // If zones change and we have a wildlife observation (not "No Animal Observation"),
      // and there's more than one zone selected, show a warning and keep only the first zone
      if (!this.isNoObservation && zones.length > 1) {
        const firstZone = zones[0];
        this.formData.selectedZones = [firstZone];
        this.showNotification('warning', 'Multiple zones are only allowed for "No Animal Observation" reports. Only the first zone has been kept.');
      }

      // We no longer reset the ski run when zone changes
      // This allows the ski run field to work independently from zone selection
    },
    submitForm() {
      // Validate all dropdown fields
      const dropdownValidationResult = this.validateAllDropdowns();
      if (!dropdownValidationResult.isValid) {
        this.formError = dropdownValidationResult.message;
        return;
      }
      
      // Validate the form
      const validationResult = validateWildlifeReport(this.formData);
      if (!validationResult.isValid) {
        this.formError = validationResult.message;
        return;
      }
      
      // Find the run ID based on the run name
      let runId = null;
      if (this.formData.ski_run && this.runs) {
        const matchingRun = this.runs.find(run => run.runname === this.formData.ski_run);
        if (matchingRun) {
          runId = matchingRun.runid;
        }
      }

      // Prepare the data for submission
      const reportData = prepareWildlifeReportData({
        ...this.formData,
        runid: runId,
        observerid: this.formData.observerid
      });

      // Emit the submit event with the prepared data
      this.$emit('submit', reportData);
    },
    cancelForm() {
      this.$emit('cancel');
    },
    showNotification(type, message) {
      this.notification = {
        show: true,
        type,
        message
      };
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        this.closeNotification();
      }, 5000);
    },
    closeNotification() {
      this.notification.show = false;
    },
    
    /**
     * Validates all dropdown selections against standardized options
     * 
     * @returns {Object} - Validation result with isValid and message properties
     */
    validateAllDropdowns() {
      // Skip validation for animal-specific fields if species is "No Observation"
      const isNoObs = this.isNoObservation;
      
      // Validate species
      const speciesResult = validateSpecies(this.formData.species);
      if (!speciesResult.isValid) {
        return speciesResult;
      }
      
      // Validate UTM Zone
      const utmZoneResult = validateUTMZone(this.formData.utm_zone);
      if (!utmZoneResult.isValid) {
        return utmZoneResult;
      }
      
      // For wildlife observations, validate animal-specific fields
      if (!isNoObs) {
        // Validate Animal Distance
        if (this.formData.animal_distance) {
          const animalDistanceResult = validateAnimalDistance(this.formData.animal_distance);
          if (!animalDistanceResult.isValid) {
            return animalDistanceResult;
          }
        }
        
        // Validate Aspect of Animal
        if (this.formData.aspect_of_animal) {
          const aspectResult = validateAspectOfAnimal(this.formData.aspect_of_animal);
          if (!aspectResult.isValid) {
            return aspectResult;
          }
        }
        
        // Validate Elevation
        if (this.formData.elevation) {
          const elevationResult = validateElevation(this.formData.elevation);
          if (!elevationResult.isValid) {
            return elevationResult;
          }
        }
        
        // Validate Animal Reaction
        if (this.formData.animal_reaction) {
          const reactionResult = validateAnimalReaction(this.formData.animal_reaction);
          if (!reactionResult.isValid) {
            return reactionResult;
          }
        }
        
        // Validate Immediate Action
        if (this.formData.immediate_action) {
          const immediateActionResult = validateAction(this.formData.immediate_action);
          if (!immediateActionResult.isValid) {
            return immediateActionResult;
          }
        }
        
        // Validate End of Day Action
        if (this.formData.end_of_day_action) {
          const endOfDayActionResult = validateAction(this.formData.end_of_day_action);
          if (!endOfDayActionResult.isValid) {
            return endOfDayActionResult;
          }
        }
      }
      
      // Validate Inventory Method
      if (this.formData.inventory_method) {
        const inventoryMethodResult = validateInventoryMethod(this.formData.inventory_method);
        if (!inventoryMethodResult.isValid) {
          return inventoryMethodResult;
        }
      }
      
      // Validate Type of Machine
      if (this.formData.type_of_machine) {
        const machineTypeResult = validateMachineType(this.formData.type_of_machine);
        if (!machineTypeResult.isValid) {
          return machineTypeResult;
        }
      }
      
      return {
        isValid: true,
        message: ''
      };
    }
  },
  created() {
    // Listen for show-notification event from child components
    this.$on('show-notification', ({ type, message }) => {
      this.showNotification(type, message);
    });

    // Fetch the staff ID for the current user
    this.fetchStaffId();
  }
};
</script>

<style scoped>
.wildlife-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group {
  flex: 1;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

input[type="text"],
input[type="number"],
input[type="date"],
textarea,
select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

textarea {
  resize: vertical;
}

.checkbox-row {
  margin-top: 10px;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-group input[type="checkbox"] {
  margin-right: 5px;
}

.checkbox-group label {
  margin-bottom: 0;
}

.validation-error {
  color: red;
  font-size: 0.8em;
  margin-top: 3px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.submit-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.submit-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.cancel-button {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.form-error {
  color: red;
  margin-top: 10px;
  text-align: center;
}

.notification {
  padding: 10px 15px;
  margin-bottom: 15px;
  border-radius: 4px;
  position: relative;
  animation: fadeIn 0.3s;
}

.notification.success {
  background-color: #d4edda;
  color: #155724;
  border-left: 4px solid #28a745;
}

.notification.warning {
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

.notification.error {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.notification.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border-left: 4px solid #17a2b8;
}

.close-notification {
  position: absolute;
  top: 5px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.2em;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
}

.close-notification:hover {
  opacity: 1;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>